class DeliveryEstimation extends HTMLElement {
  constructor() {
    super();

    this.minDateElement = null;
    this.maxDateElement = null;
    this.deliveryTextElement = null;
    this.betweenTextElement = null;

    this.currentQuantity = 1;

    this.config = {
      locale: 'fr',
      minDays: 3,
      maxDays: 5,
      noSaturday: false,
      noSunday: false,
      dateFormat: 'in_letters',
      showWeekday: true,
      cutoffHour: null,
      preorderText: '',
      preorderBetweenText: 'et le',
      preorderMinDays: 12,
      preorderMaxDays: 20,
    };
  }

  connectedCallback() {
    this.minDateElement = this.querySelector('[data-ref="min-date"]');
    this.maxDateElement = this.querySelector('[data-ref="max-date"]');
    this.deliveryTextElement = this.querySelector('[data-ref="delivery-text"]');
    this.betweenTextElement = this.querySelector('[data-ref="between-text"]');

    this.#loadConfiguration();
    this.#bindEvents();
    this.#update();
  }

  disconnectedCallback() {
    this.#unbindEvents();
  }

  #loadConfiguration() {
    this.config.locale = this.dataset.locale || 'fr';
    this.config.minDays = parseInt(this.dataset.minDays) || 3;
    this.config.maxDays = parseInt(this.dataset.maxDays) || 5;
    this.config.noSaturday = this.dataset.noSaturday === 'true';
    this.config.noSunday = this.dataset.noSunday === 'true';
    this.config.dateFormat = this.dataset.dateFormat || 'in_letters';
    this.config.showWeekday = this.dataset.showWeekday === 'true';
    this.config.preorderText = this.dataset.preorderText || '';
    this.config.preorderBetweenText = this.dataset.preorderBetweenText || 'et le';
    this.config.preorderMinDays = parseInt(this.dataset.preorderMinDays) || 14;
    this.config.preorderMaxDays = parseInt(this.dataset.preorderMaxDays) || 21;

    const cutoffHour = this.dataset.cutoffHour;
    if (cutoffHour && cutoffHour !== '') {
      this.config.cutoffHour = parseInt(cutoffHour);
    }
  }

  #bindEvents() {
    this._onFormUpdated = () => {
      this.#update();
    };

    this._onQuantityChange = (event) => {
      const qty = parseInt(event.target.value);
      if (!isNaN(qty) && qty > 0) {
        this.currentQuantity = qty;
        this.#update();
      }
    };

    // Écouter sur le product-form parent pour form:updated
    const productForm = this.closest('product-form') || document.querySelector('product-form');
    this._productForm = productForm;
    if (productForm) {
      productForm.addEventListener('form:updated', this._onFormUpdated);
    } else {
      document.addEventListener('form:updated', this._onFormUpdated);
    }

    this._bindQuantityInput();
  }

  #unbindEvents() {
    if (this._productForm) {
      this._productForm.removeEventListener('form:updated', this._onFormUpdated);
    } else {
      document.removeEventListener('form:updated', this._onFormUpdated);
    }

    if (this._quantityInput) {
      this._quantityInput.removeEventListener('change', this._onQuantityChange);
      this._quantityInput.removeEventListener('input', this._onQuantityChange);
    }

    if (this._qtySelector) {
      this._qtySelector.removeEventListener('click', this._onQtySelectorClick);
    }
  }

  _bindQuantityInput() {
    const form = this.closest('form') || document.querySelector('form[action="/cart/add"]');
    const qtyInput = form
      ? form.querySelector('[name="quantity"], [data-quantity-input], input[type="number"]')
      : document.querySelector('[name="quantity"], [data-quantity-input]');

    if (qtyInput) {
      this._quantityInput = qtyInput;
      this.currentQuantity = parseInt(qtyInput.value) || 1;
      qtyInput.addEventListener('change', this._onQuantityChange);
      qtyInput.addEventListener('input', this._onQuantityChange);
    }

    const qtySelector = form
      ? form.querySelector('quantity-selector, .quantity-selector')
      : document.querySelector('quantity-selector, .quantity-selector');

    if (qtySelector) {
      this._onQtySelectorClick = () => {
        setTimeout(() => {
          const input = qtySelector.querySelector('input');
          if (input) {
            const qty = parseInt(input.value);
            if (!isNaN(qty) && qty > 0 && qty !== this.currentQuantity) {
              this.currentQuantity = qty;
              this.#update();
            }
          }
        }, 50);
      };
      qtySelector.addEventListener('click', this._onQtySelectorClick);
      this._qtySelector = qtySelector;
    }
  }

  /**
   * Lit l'inventory depuis le bouton add-to-cart principal (data-inventory).
   * On cible [data-ref="add-to-cart-button"][data-inventory] pour exclure
   * les boutons des cross-sells qui n'ont pas cet attribut.
   */
  #getInventory() {
    const productForm = this.closest('product-form') || document.querySelector('product-form');
    if (!productForm) return null;
    const button = productForm.querySelector('[data-ref="add-to-cart-button"][data-inventory]');
    if (!button) return null;
    const val = parseInt(button.dataset.inventory, 10);
    return isNaN(val) ? null : val;
  }

  /**
   * Détermine si le variant actuel est en précommande
   * en tenant compte de la quantité sélectionnée
   */
  #isPreorder() {
    const inventory = this.#getInventory();
    if (inventory === null) return false;
    return inventory <= 0 || this.currentQuantity > inventory;
  }

  #update() {
    const isPreorder = this.#isPreorder();
    if (isPreorder) {
      this.#displayPreorder();
    } else {
      this.#displayNormal();
    }
  }

  // Méthode publique pour recalculer les dates normales (appelée depuis add-to-cart-button.liquid)
  _recalcNormalDates() {
    this.#displayNormal();
  }

  #displayNormal() {
    const minDate = this.#calculateDeliveryDate(this.config.minDays);
    const maxDate = this.#calculateDeliveryDate(this.config.maxDays);

    if (this.deliveryTextElement) {
      this.deliveryTextElement.textContent = this.deliveryTextElement.dataset.original
        || this.deliveryTextElement.textContent;
    }

    if (this.betweenTextElement) {
      this.betweenTextElement.textContent = this.betweenTextElement.dataset.original
        || this.betweenTextElement.textContent;
    }

    if (this.minDateElement) {
      this.minDateElement.textContent = this.#formatDate(minDate);
    }

    if (this.maxDateElement) {
      this.maxDateElement.textContent = this.#formatDate(maxDate);
    }
  }

  #displayPreorder() {
    // Stocker le texte original si pas encore fait
    if (this.deliveryTextElement && !this.deliveryTextElement.dataset.original) {
      this.deliveryTextElement.dataset.original = this.deliveryTextElement.textContent;
    }
    if (this.betweenTextElement && !this.betweenTextElement.dataset.original) {
      this.betweenTextElement.dataset.original = this.betweenTextElement.textContent;
    }

    const minDate = this.#calculateDeliveryDate(this.config.preorderMinDays);
    const maxDate = this.#calculateDeliveryDate(this.config.preorderMaxDays);

    if (this.deliveryTextElement) {
      this.deliveryTextElement.textContent = this.config.preorderText;
    }

    if (this.betweenTextElement) {
      this.betweenTextElement.textContent = this.config.preorderBetweenText;
    }

    if (this.minDateElement) {
      this.minDateElement.textContent = this.#formatDate(minDate);
    }

    if (this.maxDateElement) {
      this.maxDateElement.textContent = this.#formatDate(maxDate);
    }
  }

  #calculateDeliveryDate(daysToAdd) {
    const now = new Date();
    let startDate = new Date();

    if (this.config.cutoffHour !== null) {
      const currentHour = now.getHours();
      if (currentHour >= this.config.cutoffHour) {
        startDate.setDate(startDate.getDate() + 1);
      }
    }

    let businessDaysAdded = 0;
    let currentDate = new Date(startDate);

    while (businessDaysAdded < daysToAdd) {
      currentDate.setDate(currentDate.getDate() + 1);

      const dayOfWeek = currentDate.getDay();
      const isSaturday = dayOfWeek === 6;
      const isSunday = dayOfWeek === 0;

      const isBusinessDay = !((isSaturday && this.config.noSaturday) || (isSunday && this.config.noSunday));

      if (isBusinessDay) {
        businessDaysAdded++;
      }
    }

    while (this.#isNonDeliveryDay(currentDate)) {
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return currentDate;
  }

  #isNonDeliveryDay(date) {
    const dayOfWeek = date.getDay();
    const isSaturday = dayOfWeek === 6;
    const isSunday = dayOfWeek === 0;
    return (isSaturday && this.config.noSaturday) || (isSunday && this.config.noSunday);
  }

  #formatDate(date) {
    const options = {};

    if (this.config.dateFormat === 'numbers') {
      options.day = 'numeric';
      options.month = 'numeric';
    } else {
      options.day = 'numeric';
      options.month = 'long';
    }

    if (this.config.showWeekday) {
      options.weekday = 'long';
    }

    const formatter = new Intl.DateTimeFormat(this.config.locale, options);
    let formattedDate = formatter.format(date);
    return this.#capitalizeFirstLetter(formattedDate);
  }

  #capitalizeFirstLetter(str) {
    if (!str) return str;
    return str.charAt(0).toUpperCase() + str.slice(1);
  }
}

if (!customElements.get('delivery-estimation')) {
  customElements.define('delivery-estimation', DeliveryEstimation);
}